import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  Card,
  CardContent,
  IconButton,
  Fade,
  Grow,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  TrendingUp as ChartIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as LineChartIcon,
  ShowChart as AreaChartIcon,
  BubbleChart as BubbleChartIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { spacing } from '../../theme/spacing';

/**
 * Chart Loading States - Professional loading skeletons and error states:
 * - Animated loading skeletons for different chart types
 * - Beautiful error states with retry functionality
 * - Empty state designs with helpful messages
 * - Responsive layouts for all screen sizes
 * - Smooth transitions and micro-interactions
 */

// Generic Chart Loading Skeleton
export const ChartLoadingSkeleton = ({
  chartType = 'line',
  height = 400,
  title = true,
  subtitle = false,
  legend = true,
  animate = true,
  className = '',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerHeight = isMobile ? Math.min(height, 300) : height;

  // Skeleton variants for different chart types
  const renderChartSkeleton = () => {
    switch (chartType) {
      case 'line':
      case 'area':
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Y-axis lines */}
            {[...Array(5)].map((_, index) => (
              <motion.div
                key={`y-line-${index}`}
                initial={animate ? { opacity: 0, scaleX: 0 } : undefined}
                animate={animate ? { opacity: 0.3, scaleX: 1 } : undefined}
                transition={animate ? { delay: index * 0.1, duration: 0.8 } : undefined}
                style={{
                  position: 'absolute',
                  top: `${20 + index * 15}%`,
                  left: '8%',
                  right: '5%',
                  height: '1px',
                  backgroundColor: theme.palette.grey[300],
                  transformOrigin: 'left center'
                }}
              />
            ))}

            {/* Chart line path */}
            <svg
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              <motion.path
                d="M 8% 80% Q 25% 60% 40% 45% T 70% 35% T 92% 25%"
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                initial={animate ? { pathLength: 0, opacity: 0 } : undefined}
                animate={animate ? { pathLength: 1, opacity: 0.7 } : undefined}
                transition={animate ? { delay: 0.5, duration: 2, ease: "easeInOut" } : undefined}
              />
              
              {chartType === 'area' && (
                <motion.path
                  d="M 8% 80% Q 25% 60% 40% 45% T 70% 35% T 92% 25% L 92% 90% L 8% 90% Z"
                  fill={theme.palette.primary.main}
                  fillOpacity={0.2}
                  initial={animate ? { opacity: 0 } : undefined}
                  animate={animate ? { opacity: 0.3 } : undefined}
                  transition={animate ? { delay: 1.5, duration: 0.8 } : undefined}
                />
              )}

              {/* Data points */}
              {[8, 25, 40, 55, 70, 85, 92].map((x, index) => (
                <motion.circle
                  key={`point-${index}`}
                  cx={`${x}%`}
                  cy={`${80 - index * 8}%`}
                  r="4"
                  fill={theme.palette.primary.main}
                  initial={animate ? { scale: 0, opacity: 0 } : undefined}
                  animate={animate ? { scale: 1, opacity: 0.8 } : undefined}
                  transition={animate ? { delay: 2 + index * 0.1, duration: 0.3 } : undefined}
                />
              ))}
            </svg>

            {/* X-axis labels */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '5%',
                left: '8%',
                right: '5%',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              {[...Array(isMobile ? 4 : 7)].map((_, index) => (
                <motion.div
                  key={`x-label-${index}`}
                  initial={animate ? { opacity: 0, y: 10 } : undefined}
                  animate={animate ? { opacity: 1, y: 0 } : undefined}
                  transition={animate ? { delay: 1 + index * 0.1, duration: 0.5 } : undefined}
                >
                  <Skeleton
                    variant="text"
                    width={isMobile ? 30 : 40}
                    height={16}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                </motion.div>
              ))}
            </Box>

            {/* Y-axis labels */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: '15%',
                bottom: '15%',
                width: '6%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-end'
              }}
            >
              {[...Array(5)].map((_, index) => (
                <motion.div
                  key={`y-label-${index}`}
                  initial={animate ? { opacity: 0, x: -10 } : undefined}
                  animate={animate ? { opacity: 1, x: 0 } : undefined}
                  transition={animate ? { delay: 0.8 + index * 0.1, duration: 0.5 } : undefined}
                >
                  <Skeleton
                    variant="text"
                    width={25}
                    height={14}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                </motion.div>
              ))}
            </Box>
          </Box>
        );

      case 'bar':
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Grid lines */}
            {[...Array(5)].map((_, index) => (
              <motion.div
                key={`grid-${index}`}
                initial={animate ? { opacity: 0, scaleX: 0 } : undefined}
                animate={animate ? { opacity: 0.3, scaleX: 1 } : undefined}
                transition={animate ? { delay: index * 0.1, duration: 0.8 } : undefined}
                style={{
                  position: 'absolute',
                  top: `${20 + index * 15}%`,
                  left: '8%',
                  right: '5%',
                  height: '1px',
                  backgroundColor: theme.palette.grey[300],
                  transformOrigin: 'left center'
                }}
              />
            ))}

            {/* Bars */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                left: '8%',
                right: '5%',
                height: '70%',
                display: 'flex',
                alignItems: 'end',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              {[60, 80, 45, 90, 35, 70, 55].slice(0, isMobile ? 5 : 7).map((height, index) => (
                <motion.div
                  key={`bar-${index}`}
                  initial={animate ? { scaleY: 0, opacity: 0 } : undefined}
                  animate={animate ? { scaleY: 1, opacity: 0.8 } : undefined}
                  transition={animate ? { delay: 0.5 + index * 0.15, duration: 0.8, ease: "easeOut" } : undefined}
                  style={{
                    flex: 1,
                    maxWidth: isMobile ? '40px' : '60px',
                    height: `${height}%`,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '4px 4px 0 0',
                    transformOrigin: 'bottom',
                    opacity: 0.6
                  }}
                />
              ))}
            </Box>

            {/* X-axis labels */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '5%',
                left: '8%',
                right: '5%',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              {[...Array(isMobile ? 5 : 7)].map((_, index) => (
                <motion.div
                  key={`x-label-${index}`}
                  initial={animate ? { opacity: 0, y: 10 } : undefined}
                  animate={animate ? { opacity: 1, y: 0 } : undefined}
                  transition={animate ? { delay: 1.5 + index * 0.1, duration: 0.5 } : undefined}
                >
                  <Skeleton
                    variant="text"
                    width={isMobile ? 30 : 40}
                    height={16}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                </motion.div>
              ))}
            </Box>
          </Box>
        );

      case 'pie':
      case 'donut':
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              initial={animate ? { scale: 0, rotate: -90 } : undefined}
              animate={animate ? { scale: 1, rotate: 0 } : undefined}
              transition={animate ? { duration: 1.5, ease: "easeOut" } : undefined}
            >
              <svg width={isMobile ? 180 : 220} height={isMobile ? 180 : 220}>
                {/* Pie segments */}
                {[
                  { startAngle: 0, endAngle: 90, color: theme.palette.primary.main },
                  { startAngle: 90, endAngle: 180, color: theme.palette.secondary.main },
                  { startAngle: 180, endAngle: 270, color: theme.palette.success.main },
                  { startAngle: 270, endAngle: 360, color: theme.palette.warning.main }
                ].map((segment, index) => {
                  const radius = isMobile ? 80 : 100;
                  const innerRadius = chartType === 'donut' ? radius * 0.6 : 0;
                  const centerX = isMobile ? 90 : 110;
                  const centerY = isMobile ? 90 : 110;

                  const x1 = centerX + Math.cos((segment.startAngle - 90) * Math.PI / 180) * radius;
                  const y1 = centerY + Math.sin((segment.startAngle - 90) * Math.PI / 180) * radius;
                  const x2 = centerX + Math.cos((segment.endAngle - 90) * Math.PI / 180) * radius;
                  const y2 = centerY + Math.sin((segment.endAngle - 90) * Math.PI / 180) * radius;

                  const largeArcFlag = segment.endAngle - segment.startAngle > 180 ? 1 : 0;

                  let pathData;
                  if (chartType === 'donut') {
                    const ix1 = centerX + Math.cos((segment.startAngle - 90) * Math.PI / 180) * innerRadius;
                    const iy1 = centerY + Math.sin((segment.startAngle - 90) * Math.PI / 180) * innerRadius;
                    const ix2 = centerX + Math.cos((segment.endAngle - 90) * Math.PI / 180) * innerRadius;
                    const iy2 = centerY + Math.sin((segment.endAngle - 90) * Math.PI / 180) * innerRadius;

                    pathData = [
                      `M ${x1} ${y1}`,
                      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      `L ${ix2} ${iy2}`,
                      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${ix1} ${iy1}`,
                      'Z'
                    ].join(' ');
                  } else {
                    pathData = [
                      `M ${centerX} ${centerY}`,
                      `L ${x1} ${y1}`,
                      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      'Z'
                    ].join(' ');
                  }

                  return (
                    <motion.path
                      key={`segment-${index}`}
                      d={pathData}
                      fill={segment.color}
                      opacity={0.7}
                      initial={animate ? { pathLength: 0, opacity: 0 } : undefined}
                      animate={animate ? { pathLength: 1, opacity: 0.7 } : undefined}
                      transition={animate ? { delay: index * 0.3, duration: 0.8 } : undefined}
                    />
                  );
                })}
              </svg>
            </motion.div>
          </Box>
        );

      default:
        // Generic chart skeleton
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{
                bgcolor: theme.palette.grey[200],
                borderRadius: 1
              }}
            />
            <motion.div
              initial={animate ? { opacity: 0 } : undefined}
              animate={animate ? { opacity: [0, 0.5, 0] } : undefined}
              transition={animate ? { repeat: Infinity, duration: 2 } : undefined}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <ChartIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
            </motion.div>
          </Box>
        );
    }
  };

  return (
    <Card className={className} sx={{ height: containerHeight, ...sx }} {...props}>
      <CardContent sx={{ height: '100%', p: spacing.component.padding.md }}>
        {/* Title skeleton */}
        {title && (
          <motion.div
            initial={animate ? { opacity: 0, y: -10 } : undefined}
            animate={animate ? { opacity: 1, y: 0 } : undefined}
            transition={animate ? { duration: 0.5 } : undefined}
          >
            <Skeleton
              variant="text"
              width="60%"
              height={isMobile ? 28 : 32}
              sx={{ mb: 1, bgcolor: theme.palette.grey[300] }}
            />
          </motion.div>
        )}

        {/* Subtitle skeleton */}
        {subtitle && (
          <motion.div
            initial={animate ? { opacity: 0, y: -10 } : undefined}
            animate={animate ? { opacity: 1, y: 0 } : undefined}
            transition={animate ? { delay: 0.2, duration: 0.5 } : undefined}
          >
            <Skeleton
              variant="text"
              width="40%"
              height={20}
              sx={{ mb: 2, bgcolor: theme.palette.grey[300] }}
            />
          </motion.div>
        )}

        {/* Chart area */}
        <Box
          sx={{
            height: `calc(100% - ${title ? (subtitle ? '80px' : '50px') : '20px'})`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {renderChartSkeleton()}
        </Box>

        {/* Legend skeleton */}
        {legend && (
          <motion.div
            initial={animate ? { opacity: 0, y: 10 } : undefined}
            animate={animate ? { opacity: 1, y: 0 } : undefined}
            transition={animate ? { delay: 2, duration: 0.5 } : undefined}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                mt: 2,
                flexWrap: 'wrap'
              }}
            >
              {[...Array(isMobile ? 2 : 4)].map((_, index) => (
                <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton
                    variant="circular"
                    width={12}
                    height={12}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                  <Skeleton
                    variant="text"
                    width={60}
                    height={16}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                </Box>
              ))}
            </Box>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

// Chart Error State
export const ChartErrorState = ({
  error,
  onRetry,
  height = 400,
  title = 'Chart Error',
  showDetails = false,
  retryLabel = 'Retry',
  className = '',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getErrorMessage = () => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    return 'Failed to load chart data';
  };

  const getErrorIcon = () => {
    const iconProps = { 
      sx: { fontSize: isMobile ? 48 : 64, color: theme.palette.error.main, mb: 2 } 
    };
    
    if (error?.type === 'network') return <ErrorIcon {...iconProps} />;
    if (error?.type === 'data') return <BarChartIcon {...iconProps} />;
    return <ErrorIcon {...iconProps} />;
  };

  return (
    <Card className={className} sx={{ height, ...sx }} {...props}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: spacing.component.padding.lg
        }}
      >
        <Fade in timeout={800}>
          <Box>
            {getErrorIcon()}
            
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 400 }}
            >
              {getErrorMessage()}
            </Typography>

            {showDetails && error?.details && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="caption" component="pre">
                  {JSON.stringify(error.details, null, 2)}
                </Typography>
              </Alert>
            )}

            {onRetry && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onRetry}
                  size={isMobile ? 'medium' : 'large'}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5
                  }}
                >
                  {retryLabel}
                </Button>
              </motion.div>
            )}
          </Box>
        </Fade>
      </CardContent>
    </Card>
  );
};

// Chart Empty State
export const ChartEmptyState = ({
  message = 'No data available',
  description = 'There is no data to display in this chart.',
  action,
  actionLabel = 'Load Data',
  height = 400,
  icon: CustomIcon,
  className = '',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const DefaultIcon = CustomIcon || BarChartIcon;

  return (
    <Card className={className} sx={{ height, ...sx }} {...props}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: spacing.component.padding.lg
        }}
      >
        <Grow in timeout={1000}>
          <Box>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <DefaultIcon
                sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: theme.palette.grey[400],
                  mb: 2
                }}
              />
            </motion.div>
            
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
            >
              {message}
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 400 }}
            >
              {description}
            </Typography>

            {action && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Button
                  variant="outlined"
                  onClick={action}
                  size={isMobile ? 'medium' : 'large'}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5
                  }}
                >
                  {actionLabel}
                </Button>
              </motion.div>
            )}
          </Box>
        </Grow>
      </CardContent>
    </Card>
  );
};

// Metrics Card Loading Skeleton
export const MetricsCardSkeleton = ({
  count = 4,
  animate = true,
  className = '',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      className={className}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: `repeat(${Math.min(count, 4)}, 1fr)`
        },
        gap: spacing.layout.gap.md,
        ...sx
      }}
      {...props}
    >
      {[...Array(count)].map((_, index) => (
        <motion.div
          key={`metric-skeleton-${index}`}
          initial={animate ? { opacity: 0, y: 20 } : undefined}
          animate={animate ? { opacity: 1, y: 0 } : undefined}
          transition={animate ? { delay: index * 0.1, duration: 0.6 } : undefined}
        >
          <Card sx={{ p: spacing.component.padding.md, height: isMobile ? 120 : 140 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={20}
                    sx={{ mb: 1, bgcolor: theme.palette.grey[300] }}
                  />
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={isMobile ? 28 : 32}
                    sx={{ bgcolor: theme.palette.grey[300] }}
                  />
                </Box>
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ bgcolor: theme.palette.grey[300] }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={6}
                  sx={{ borderRadius: 3, bgcolor: theme.palette.grey[300] }}
                />
                <Skeleton
                  variant="text"
                  width="40%"
                  height={16}
                  sx={{ bgcolor: theme.palette.grey[300] }}
                />
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </Box>
  );
};

export default {
  ChartLoadingSkeleton,
  ChartErrorState,
  ChartEmptyState,
  MetricsCardSkeleton
}; 